import { Form, Field, FieldProps } from "formik"
import { ChangeEvent, FC, useState } from "react"
import { InputRangeWithField } from "./InputRangeWithField"
import { isNaN } from "lodash"
import { ArrowLeftIcon } from "../../icons/ArrowLeftIcon"
import { ArrowRightIcon } from "../../icons/ArrowRightIcon"
import { InputRadioGroup } from "./InputRadioGroup"
import { useMediaQuery } from "react-responsive"
import { InputField } from "./InputField"
import { CustomSelect } from "../CustomSelect"
import { ProductValues, SVFFundValues, TDFFundValues } from "../../defines/SelectValues"
import { formatToNumber } from "../../utils/dataFormat"

export type DashboardFormProps = {
  /**
   * Existing response data used to display monthly payment
   */
  responseData?: any
  /**
   * Form errors
   */
  errors: any
  /**
   * Field of values which indicates if a field was interacted with
   */
  touched: any
  /**
   * Form values
   */
  values?: any
  /**
   * Form handle change function
   */
  handleChange: (
    event: ChangeEvent<HTMLInputElement> | { target: { name: string; value: { id: number; value: string } } }
  ) => void
  /**
   * Field handle set field value
   */
  setFieldValue: any
  /**
   * Sets all fields to readonly
   */
  readOnly?: boolean
  /**
   * Function used to switch between tabs
   */
  setTabIndex?: (value: number) => void
  /**
   * Formik function used to reset form to initial values
   */
  resetForm?: () => void
  /*
   * Formik's handle blur event
   */
  handleBlur?: (e: React.FocusEvent<any, Element>) => void
}

const maxSteps = 4

export const DashboardForm: FC<DashboardFormProps> = ({
  responseData = null,
  errors,
  touched,
  values = {},
  handleChange,
  setFieldValue,
  readOnly = false,
  setTabIndex = () => {},
  resetForm = () => {},
  handleBlur = () => {},
}) => {
  const [step, setStep] = useState<number>(1)

  const validateNumber = (n: any, min?: number, max?: number) => {
    if (isNaN(parseFloat(n)) && !isNaN(n - 0)) {
      return "Please enter a number."
    }

    if ((min || min === 0) && max) {
      if (min > +n || max < +n) {
        return `Value needs to be between ${min} and ${max}`
      }
    }
  }

  const isMobile = useMediaQuery({ query: "(max-width: 1024px)" })

  const renderProgressBar = () => {
    return (
      <div className={`relative mx-auto w-full max-w-sm ${isMobile ? "mb-8" : "mb-10"}`}>
        <div
          className={`absolute left-0 transition-all bg-brand-light h-2.5 ${
            step >= maxSteps ? "rounded-full" : "rounded-l-full"
          }`}
          style={{ width: `${(step / maxSteps) * 100}%` }}
        />
        <div className="absolute left-0 w-full bg-brand-light/20 opacity-20 rounded-full h-2.5 " />
      </div>
    )
  }

  const renderFormElements = () => {
    switch (step) {
      case 1:
        return (
          <>
            <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
              <h4 className="text-xl font-bold">Assets</h4>
            </div>
            <Field name="TotalAssets" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputRangeWithField
                    id="TotalAssets"
                    label="Takeover Assets"
                    value={field.value}
                    handleChange={(e) => setFieldValue("TotalAssets", formatToNumber(e.target.value))}
                    min={100000}
                    max={10000000}
                    step={100000}
                    error={errors["TotalAssets"]}
                    touched={touched["TotalAssets"]}
                    type="currency"
                    readOnly={readOnly}
                  />
                </div>
              )}
            </Field>

            <Field name="RolloverContributions" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputRangeWithField
                    id="RolloverContributions"
                    label="Rollover Contributions"
                    value={field.value}
                    handleChange={(e) => setFieldValue("RolloverContributions", formatToNumber(e.target.value))}
                    min={0}
                    max={5000000}
                    step={100000}
                    error={errors["RolloverContributions"]}
                    touched={touched["RolloverContributions"]}
                    type="currency"
                    readOnly={readOnly}
                  />
                </div>
              )}
            </Field>

            <Field name="Deposit_Annual" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputRangeWithField
                    id="Deposit_Annual"
                    label="Annual Flow"
                    value={field.value}
                    handleChange={(e) => setFieldValue("Deposit_Annual", formatToNumber(e.target.value))}
                    min={10000}
                    max={2000000}
                    step={10000}
                    error={errors["Deposit_Annual"]}
                    touched={touched["Deposit_Annual"]}
                    type="currency"
                    readOnly={readOnly}
                  />
                </div>
              )}
            </Field>
          </>
        )
      case 2:
        return (
          <>
            <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
              <h4 className="text-xl font-bold">Participants</h4>
            </div>
            <Field name="Active_Ppts" validate={(value: any) => validateNumber(value, 0, 10000)}>
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputField
                    id="Active_Ppts"
                    label="Active Participants"
                    placeholder="# of active participants"
                    value={field.value}
                    handleChange={(e) => setFieldValue("Active_Ppts", e.target.value)}
                    error={errors["Active_Ppts"]}
                    touched={touched["Active_Ppts"]}
                    type="number"
                    handleBlur={handleBlur}
                  />
                </div>
              )}
            </Field>

            <Field name="Inactive_Ppts" validate={(value: any) => validateNumber(value, 0, 10000)}>
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputField
                    id="Inactive_Ppts"
                    label="Inactive Participants"
                    placeholder="# of inactive participants"
                    value={field.value}
                    handleChange={(e) => setFieldValue("Inactive_Ppts", e.target.value)}
                    handleBlur={handleBlur}
                    error={errors["Inactive_Ppts"]}
                    touched={touched["Inactive_Ppts"]}
                    type="number"
                  />
                </div>
              )}
            </Field>

            <Field name="Eligible_EEs" validate={(value: any) => validateNumber(value, 100, 10000)}>
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputField
                    id="Eligible_EEs"
                    label="Eligible Employees"
                    value={field.value}
                    handleBlur={handleBlur}
                    placeholder="# of eligible employees"
                    handleChange={(e) => setFieldValue("Eligible_EEs", e.target.value)}
                    error={errors["Eligible_EEs"]}
                    touched={touched["Eligible_EEs"]}
                    type="number"
                  />
                </div>
              )}
            </Field>
          </>
        )
      case 3:
        return (
          <>
            <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
              <h4 className="text-xl font-bold">Defined Contribution (DC)</h4>
            </div>
            <Field name="Product" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <CustomSelect
                    values={ProductValues}
                    label="Product"
                    selected={field.value}
                    handleChange={(newValue: string) => setFieldValue("Product", newValue)}
                  />
                </div>
              )}
            </Field>

            <Field name="ServiceModel">
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputRadioGroup
                    id="ServiceModel"
                    label="Service Model"
                    options={["Service Basic", "Service Plus", "Service Premium"]}
                    value={field.value}
                    handleChange={(val) => setFieldValue("ServiceModel", val)}
                    isMobile={isMobile}
                  />
                </div>
              )}
            </Field>

            <Field name="Fund_Type">
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputRadioGroup
                    id="Fund_Type"
                    label="Fund Type"
                    options={["Large Market", "Mid Market"]}
                    value={field.value}
                    handleChange={(val) => setFieldValue("Fund_Type", val)}
                    isMobile={isMobile}
                  />
                </div>
              )}
            </Field>

            <Field name="SV2_Fund" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <CustomSelect
                    values={SVFFundValues}
                    label="SVF Fund Option"
                    selected={field.value}
                    handleChange={(newValue: string) => setFieldValue("SV2_Fund", newValue)}
                  />
                </div>
              )}
            </Field>

            <Field name="TDF_Fund_Name" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <CustomSelect
                    values={TDFFundValues}
                    label="TDF Fund Option"
                    selected={field.value}
                    handleChange={(newValue: string) => setFieldValue("TDF_Fund_Name", newValue)}
                  />
                </div>
              )}
            </Field>
          </>
        )

      case 4:
        return (
          <>
            <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
              <h4 className="text-xl font-bold">Fee</h4>
            </div>
            <Field name="Fee_Deposit" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputRangeWithField
                    id="Fee_Deposit"
                    label="Deposit Based SFI"
                    value={field.value}
                    handleChange={(e) => {
                      setFieldValue("Fee_Deposit", formatToNumber(e.target.value))
                    }}
                    min={0}
                    max={0.01}
                    step={0.0005}
                    error={errors["Fee_Deposit"]}
                    touched={touched["Fee_Deposit"]}
                    type="percentage"
                    readOnly={readOnly}
                  />
                </div>
              )}
            </Field>

            <Field name="Fee_Asset" validate={validateNumber}>
              {({ field }: FieldProps) => (
                <div className={`mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
                  <InputRangeWithField
                    id="Fee_Asset"
                    label="Asset Based SFI"
                    value={field.value}
                    handleChange={(e) => setFieldValue("Fee_Asset", formatToNumber(e.target.value))}
                    min={0}
                    max={0.001}
                    step={0.00005}
                    error={errors["Fee_Asset"]}
                    touched={touched["Fee_Asset"]}
                    type="percentage"
                    readOnly={readOnly}
                  />
                </div>
              )}
            </Field>
          </>
        )
    }
  }

  const renderNavigationButtons = () => {
    const updateStep = (newStepValue: number) => {
      if (newStepValue <= 1) {
        setStep(1)
      } else if (newStepValue >= maxSteps) {
        setStep(maxSteps)
      } else {
        setStep(newStepValue)
      }
    }

    return (
      <div className={`flex space-x-2 mb-4 mx-auto w-full ${isMobile ? "max-w-sm" : "max-w-full"}`}>
        <button
          className={`flex items-center justify-center h-12 w-1/2 bg-brand-light rounded cursor-pointer ${
            step === 1 && "opacity-20 cursor-not-allowed"
          } ${step < maxSteps ? "w-1/2" : "w-12"}`}
          type="button"
          onClick={() => updateStep(step - 1)}
        >
          <ArrowLeftIcon />
        </button>
        {step < maxSteps ? (
          <button
            className="flex items-center justify-center h-12 w-1/2 bg-brand-light rounded cursor-pointer"
            type="button"
            onClick={(e) => {
              // prevents weird submit error when changing steps
              e.preventDefault()
              updateStep(step + 1)
            }}
          >
            <ArrowRightIcon />
          </button>
        ) : (
          <button
            className="flex items-center justify-center h-12 w-full bg-brand-light rounded cursor-pointer text-white"
            type="submit"
          >
            CALCULATE
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={`flex flex-col items-center justify-center`}>
      {!readOnly && (
        <div className="mb-4 mx-auto w-full max-w-sm text-center">
          <div className="text-sm">
            <b>Enter your details below</b>
            <p className="mt-1">Complete 4 steps</p>
          </div>
        </div>
      )}

      {renderProgressBar()}

      <Form className="pb-4 space-y-4 w-full">
        {renderFormElements()}
        {renderNavigationButtons()}
      </Form>

      <p
        className="my-2 underline text-xs cursor-pointer"
        onClick={() => {
          resetForm()
          setStep(1)
        }}
      >
        New Quote
      </p>
    </div>
  )
}

